import React from 'react'
import { ROW_NUMBER_COLUMN_WIDTH, ROW_NUMBER_COLUMN_WIDTH_CLASS, SMALL_ROW_HEIGHT_CLASS } from 'style/constants'
import { Plus } from 'components/icons'
import { IColumnValuesCount, IViewPortColumn } from 'components/spreadsheet/types'
import { ITableRow, ITableViewColour } from 'types'
import { getRowValue } from 'components/spreadsheet/helpers/functions'
import { getCellColour, getRowLevelColour } from 'components/spreadsheet/helpers/colouring'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { useThemedColours } from 'hooks/useTheme'
import { addRowGroupCellStyles } from 'components/spreadsheet/components/addRow/styles'

interface Props {
  rowNumber: number
  addRowOffset: number
  columns: IViewPortColumn[]
  treeColumnWidth: number
  treeLeafColumn?: IViewPortColumn | null
  lastGroupRow?: ITableRow
  colourSettings?: ITableViewColour[]
  columnValuesCount: IColumnValuesCount
}

const AddRowFrozenOverlay: React.FC<Props> = ({
  rowNumber,
  addRowOffset,
  columns,
  treeColumnWidth,
  treeLeafColumn,
  lastGroupRow,
  colourSettings,
  columnValuesCount
}) => {
  const themed = useThemedColours()
  const { spreadsheetData, selectedColumnIds } = useDataContext()

  const columnLookup = new Map(spreadsheetData.viewDetails.columns.map((col) => [col.publicId, col]))
  const hasRowLevelRules = colourSettings?.some((setting) => setting.applyToEntireRow) ?? false
  const rowLevelColour =
    hasRowLevelRules && lastGroupRow
      ? getRowLevelColour(
          colourSettings!,
          lastGroupRow,
          columnLookup,
          spreadsheetData.columnIdToIndex,
          columnValuesCount,
          true,
          undefined,
          spreadsheetData.columnStore
        )
      : null

  const frozenGroupedCells = columns.filter((c) => c.isGrouped && c.isFrozen)
  const isTreeView = spreadsheetData.viewDetails.treeGroupView && treeColumnWidth > 0

  return (
    <div
      className="absolute top-0 inline-flex pointer-events-none z-[3]"
      style={{
        width: 'var(--grid-width)',
        transform: `translateY(calc(var(--header-height) + ${rowNumber} * var(--row-height) + var(--row-top-offset) - ${addRowOffset}px))`
      }}
    >
      {/* Over the add-row's own row-number cell, so it carries the same muted
          +. Clicks pass through this overlay to the add-row beneath. */}
      <div
        className={`sticky left-0 z-[4] flex items-center justify-center bg-surface text-muted [border-right:1px_solid_var(--color-divider)] [border-top:1px_solid_var(--color-divider)] [&>svg]:w-3 [&>svg]:h-3 ${SMALL_ROW_HEIGHT_CLASS} ${ROW_NUMBER_COLUMN_WIDTH_CLASS}`}
      >
        <Plus aria-hidden strokeWidth={1.5} />
      </div>

      {treeColumnWidth > 0 && treeLeafColumn && (
        <div
          className={`sticky z-[4] ${isTreeView ? '[border-top:1px_solid_var(--color-divider)]' : '[border-right:1px_solid_var(--color-divider)] [border-top:1px_solid_var(--color-divider)]'} ${SMALL_ROW_HEIGHT_CLASS}`}
          style={{
            width: `${treeColumnWidth}px`,
            left: `${ROW_NUMBER_COLUMN_WIDTH}px`,
            backgroundColor: isTreeView
              ? 'var(--color-surface-sunken)'
              : lastGroupRow && treeLeafColumn
                ? themed.fill(
                    getCellColour(
                      treeLeafColumn.column,
                      columnValuesCount,
                      lastGroupRow,
                      getRowValue(
                        lastGroupRow,
                        treeLeafColumn.column.publicId,
                        spreadsheetData.columnIdToIndex,
                        spreadsheetData.columnStore
                      ),
                      colourSettings
                    )?.backgroundColour
                  ) || 'var(--color-surface-sunken)'
                : 'var(--color-surface-sunken)'
          }}
        ></div>
      )}

      {frozenGroupedCells.map((c) => {
        const cellValue = lastGroupRow
          ? getRowValue(lastGroupRow, c.column.publicId, spreadsheetData.columnIdToIndex, spreadsheetData.columnStore)
          : undefined
        const cellColour = lastGroupRow
          ? getCellColour(
              c.column,
              columnValuesCount,
              lastGroupRow,
              cellValue,
              colourSettings,
              true,
              undefined,
              rowLevelColour
            )
          : null
        return (
          <div
            key={`frozen-overlay-${c.column.publicId}`}
            data-testid="add-row-group-cell"
            data-columnid={c.column.publicId}
            className={addRowGroupCellStyles({
              frozen: true,
              lastFrozen: c.isLastFrozenColumn,
              inSelectedColumn: selectedColumnIds.includes(c.column.publicId)
            })}
            style={{
              width: `${c.column.width}px`,
              backgroundColor: themed.fill(cellColour?.backgroundColour) || 'var(--color-surface-sunken)',
              left: `${c.left}px`
            }}
          />
        )
      })}
    </div>
  )
}

export default AddRowFrozenOverlay
